import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';

export const DISPOSITION_VIEWS = ['tag', 'woche'] as const;
export type DispositionView = (typeof DISPOSITION_VIEWS)[number];

export interface DispositionViewState {
  /** Which view is actually on screen — already corrected for planning mode. */
  value: DispositionView;
  setValue: (view: DispositionView) => void;
  /**
   * False while a planning or reschedule deep link is open. The switch hides rather than
   * disabling: there is nothing the user did wrong, the week view simply cannot place a block.
   */
  canSwitch: boolean;
}

/**
 * Which disposition view is showing, persisted as `?view=tag|woche`.
 *
 * The week is **read-only**, so anything that needs the real board — placing a Termin, moving a
 * displaced Aufgabe — forces `tag`. That check reads `disposition-mode` directly instead of calling
 * `useDispositionMode`, so the widget layer does not have to reach into
 * `features/disposition-planning` for one boolean.
 *
 * Options match the rest of the page: replace rather than push, so the back button leaves the
 * calendar instead of walking through every toggle.
 */
export function useDispositionView(): DispositionViewState {
  const [rawView, setView] = useQueryState(
    'view',
    parseAsStringLiteral(DISPOSITION_VIEWS).withDefault('tag').withOptions({
      history: 'replace',
      shallow: true,
      clearOnDefault: true,
    }),
  );

  const [mode] = useQueryState('disposition-mode', parseAsString);
  const canSwitch = mode == null;

  return {
    value: canSwitch ? rawView : 'tag',
    setValue: (view) => {
      void setView(view);
    },
    canSwitch,
  };
}
